/**
 * 📬 WARTELISTEN-HELPER
 * Funktionen für das Matching von wartenden Kunden zu neuen Paketen
 * 
 * v2.0 - Verbessertes Fuzzy-Matching:
 * - Hausnummer ist kein Pflicht-Gate mehr, sondern Bonus
 * - Name wird mit 70% gewichtet (wortbasiert + tolerant bei OCR-Fehlern)
 * - Adresse wird mit 30% gewichtet (Straße+Nr als Ganzes verglichen)
 * - Jaro-Winkler-ähnliche Distanz für bessere Kurzwort-Erkennung
 */

import { getWaitingList as serverGetWaitingList, patchWaitingListEntry } from './services/serverDataService';
import { getLocalAddressBook, isAddressBookEnabled } from './services/addressBookService';

// ============================================
// 📐 STRING-DISTANZ-FUNKTIONEN
// ============================================

/**
 * Berechnet die Levenshtein-Distanz zwischen zwei Strings
 */
function levenshteinDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Jaro-Ähnlichkeit (0-1) — besser für kurze Strings als Levenshtein
 * "Mustermann" vs "Mustennann" → 0.87 (Levenshtein wäre nur 0.80)
 */
function jaroSimilarity(s1, s2) {
  if (s1 === s2) return 1;
  if (!s1.length || !s2.length) return 0;

  const matchWindow = Math.max(0, Math.floor(Math.max(s1.length, s2.length) / 2) - 1);
  const s1Matches = new Array(s1.length).fill(false);
  const s2Matches = new Array(s2.length).fill(false);

  let matches = 0;
  let transpositions = 0;

  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, s2.length);
    for (let j = start; j < end; j++) {
      if (s2Matches[j] || s1[i] !== s2[j]) continue;
      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }

  if (matches === 0) return 0;

  let k = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!s1Matches[i]) continue;
    while (!s2Matches[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }

  return (
    matches / s1.length +
    matches / s2.length +
    (matches - transpositions / 2) / matches
  ) / 3;
}

/**
 * Jaro-Winkler-Ähnlichkeit — gibt Bonus für gemeinsamen Prefix
 * Besonders gut für Namen: "Max Mustermann" vs "Max Mustennann"
 */
function jaroWinklerSimilarity(s1, s2) {
  const jaro = jaroSimilarity(s1, s2);
  
  // Gemeinsamer Prefix (max 4 Zeichen)
  let prefixLen = 0;
  for (let i = 0; i < Math.min(4, s1.length, s2.length); i++) {
    if (s1[i] === s2[i]) prefixLen++;
    else break;
  }
  
  return jaro + prefixLen * 0.1 * (1 - jaro);
}

/**
 * Normalisiert einen String (Kleinbuchstaben, Umlaute auflösen)
 */
function normalizeString(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Normalisiert eine Hausnummer für Vergleich
 */
function normalizeHausnummer(hnr) {
  if (!hnr) return '';
  return String(hnr)
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/^0+/, '');
}

// ============================================
// 📊 ÄHNLICHKEITS-BERECHNUNG
// ============================================

/**
 * Berechnet die Ähnlichkeit zwischen zwei Strings (0-100%)
 * Nutzt das Maximum aus Levenshtein und Jaro-Winkler
 */
function calculateSimilarity(str1, str2) {
  if (!str1 || !str2) return 0;
  
  const norm1 = normalizeString(str1);
  const norm2 = normalizeString(str2);
  
  if (norm1 === norm2) return 100;
  
  const maxLength = Math.max(norm1.length, norm2.length);
  if (maxLength === 0) return 0;
  
  // Levenshtein-basiert
  const levDist = levenshteinDistance(norm1, norm2);
  const levSim = ((maxLength - levDist) / maxLength) * 100;
  
  // Jaro-Winkler (besser für kurze Strings und Tippfehler)
  const jwSim = jaroWinklerSimilarity(norm1, norm2) * 100;
  
  return Math.round(Math.max(levSim, jwSim));
}

/**
 * Verbesserte Name-Ähnlichkeit
 * Berücksichtigt: Wort-Matching, Vor-/Nachname vertauscht, OCR-Fehler
 * 
 * Beispiele:
 * "Max Mustermann" vs "Max Mustennann" → ~85% (OCR-Fehler toleriert)
 * "Max Mustermann" vs "Mustermann Max" → 100% (Reihenfolge egal)
 * "Max Mustermann" vs "M. Mustermann" → ~75% (Abkürzung erkannt)
 */
function calculateNameSimilarity(paketName, wartelisteName) {
  if (!paketName || !wartelisteName) return 0;
  
  const norm1 = normalizeString(paketName);
  const norm2 = normalizeString(wartelisteName);
  
  if (norm1 === norm2) return 100;
  
  // Teile in Wörter (min 1 Zeichen, damit "M." als "m" noch matcht)
  const words1 = norm1.split(/\s+/).filter(w => w.length >= 1);
  const words2 = norm2.split(/\s+/).filter(w => w.length >= 1);
  
  if (words1.length === 0 || words2.length === 0) {
    return calculateSimilarity(paketName, wartelisteName);
  }
  
  // Wort-Matching: Jedes Wort der Warteliste gegen Paket-Name prüfen
  let totalWordScore = 0;
  
  for (const word2 of words2) {
    let bestWordScore = 0;
    
    for (const word1 of words1) {
      // Exakte Übereinstimmung
      if (word1 === word2) {
        bestWordScore = 100;
        break;
      }
      
      // Enthaltung (z.B. "Muster" in "Mustermann")
      if (word1.includes(word2) || word2.includes(word1)) {
        const shorter = Math.min(word1.length, word2.length);
        const longer = Math.max(word1.length, word2.length);
        bestWordScore = Math.max(bestWordScore, (shorter / longer) * 100);
        continue;
      }
      
      // Prefix-Match (z.B. "M" vs "Mustermann" — Abkürzung)
      if (word2.length <= 2 && word1.startsWith(word2)) {
        bestWordScore = Math.max(bestWordScore, 60);
        continue;
      }
      if (word1.length <= 2 && word2.startsWith(word1)) {
        bestWordScore = Math.max(bestWordScore, 60);
        continue;
      }
      
      // Jaro-Winkler Wort-Ähnlichkeit (tolerant bei OCR-Fehlern)
      const jwSim = jaroWinklerSimilarity(word1, word2) * 100;
      bestWordScore = Math.max(bestWordScore, jwSim);
      
      // Levenshtein für ähnliche Wörter (max 2-3 Zeichen Unterschied)
      const dist = levenshteinDistance(word1, word2);
      const maxLen = Math.max(word1.length, word2.length);
      if (dist <= 3 && dist < maxLen * 0.4) {
        const levSim = ((maxLen - dist) / maxLen) * 100;
        bestWordScore = Math.max(bestWordScore, levSim);
      }
    }
    
    totalWordScore += bestWordScore;
  }
  
  // Durchschnittliche Wort-Ähnlichkeit
  const wordAvgScore = totalWordScore / words2.length;
  
  // Gesamt-String Jaro-Winkler (fängt Fälle ab, wo Wörter zusammengeschrieben sind)
  const fullJW = jaroWinklerSimilarity(norm1, norm2) * 100;
  
  // Gesamt-String Levenshtein
  const fullLev = calculateSimilarity(paketName, wartelisteName);
  
  // Bestes Ergebnis aus allen drei Methoden
  return Math.round(Math.max(wordAvgScore, fullJW, fullLev));
}

/**
 * Berechnet Adress-Ähnlichkeit
 * Vergleicht gesamtes streetAndNumber mit strasse+hausnummer der Warteliste
 */
function calculateAddressSimilarity(paketAddress, entryStrasse, entryHausnummer) {
  if (!paketAddress) return 0;
  
  // Wartelisten-Adresse zusammenbauen
  const entryAddress = [entryStrasse, entryHausnummer].filter(Boolean).join(' ');
  if (!entryAddress) return 0;
  
  // Standard-Ähnlichkeit (Jaro-Winkler + Levenshtein)
  const baseSim = calculateSimilarity(paketAddress, entryAddress);
  
  // Bonus: Hausnummer-Match prüfen (extrahiert aus Paket-Adresse)
  const paketHnr = extractHausnummer(paketAddress);
  const entryHnr = normalizeHausnummer(entryHausnummer);
  const hausnummerMatch = paketHnr && entryHnr && normalizeHausnummer(paketHnr) === entryHnr;
  
  // Wenn Hausnummer stimmt, ist die Adresse deutlich vertrauenswürdiger
  if (hausnummerMatch) {
    return Math.min(100, baseSim + 20);
  }
  
  return baseSim;
}

// ============================================
// 📬 HAUPT-MATCHING-FUNKTION
// ============================================

/**
 * Prüft ob das neue Paket zu wartenden Kunden passt
 * 
 * Matching-Regeln (v2.0):
 * - Name: 70% Gewichtung (wortbasiert, tolerant bei OCR-Fehlern)
 * - Adresse: 30% Gewichtung (Straße+Nr als Ganzes, Hausnummer als Bonus)
 * - Schwellwert: >= 50% Ähnlichkeit
 * - Hausnummer ist kein Pflicht-Gate mehr!
 * 
 * @param {Object} neuesPaket - Das neu gespeicherte Paket
 * @returns {Array} - Liste von Matches mit Scores
 */
export async function findMatchingWaitingCustomers(neuesPaket) {
  try {
    console.log('📬 [Matching] Prüfe Warteliste für Paket:', neuesPaket.name, neuesPaket.streetAndNumber);
    const addressBookOn = isAddressBookEnabled();

    // Lade wartende Einträge (+ dauerhafte Adressbuch-Kontakte nur, wenn Adressbuch aktiv ist)
    let waitingList = [];
    try {
      const allEntries = await serverGetWaitingList();
      waitingList = allEntries.filter(e => {
        if (e.active === false) return false;
        const isPerm = e.status === 'dauerhaft' || String(e.firebase_id || e.id || '').startsWith('addr_');
        if (isPerm) return addressBookOn;
        return e.status === 'wartend' || !e.status;
      });
    } catch (err) {
      console.error('📬 [Matching] Fehler beim Laden der Warteliste:', err);
    }

    // Lokales Adressbuch ergänzen (nur wenn im Setup aktiviert)
    if (addressBookOn) {
      try {
        const localContacts = getLocalAddressBook();
        const existingById = new Map(waitingList.map(e => [String(e.firebase_id || e.id || ''), e]));
        for (const c of localContacts) {
          if (!c || !c.name) continue;
          if (existingById.has(c.id)) {
            const ex = existingById.get(c.id);
            if (c.note && !ex.note) ex.note = c.note;
            if (c.shelf && !ex.shelf) ex.shelf = c.shelf;
            continue;
          }
          waitingList.push({
            id: c.id,
            firebase_id: c.id,
            name: c.name,
            strasse: c.department || '',
            hausnummer: c.shelf || '',
            shelf: c.shelf || '',
            note: c.note || '',
            kontakt: c.email || '',
            kontakt_typ: 'email',
            kontaktTyp: 'email',
            status: 'dauerhaft',
            active: true,
          });
        }
      } catch { /* */ }
    }

    console.log(`📬 [Matching] ${waitingList.length} wartende Kunden / Adressbuch-Kontakte gefunden (Liste aktiv=${addressBookOn})`);
    if (waitingList.length === 0) return [];

    const matches = [];

    for (const entry of waitingList) {
      const isPermanent = entry.status === 'dauerhaft' || String(entry.firebase_id || entry.id || '').startsWith('addr_');
      let entryNote = entry.note || '';
      if (!entryNote && entry.notizen) {
        try {
          const parsedMeta = JSON.parse(entry.notizen);
          if (parsedMeta && parsedMeta.note) entryNote = String(parsedMeta.note).trim();
        } catch { /* */ }
      }

      // 1️⃣ NAME: Ähnlichkeit berechnen (70% Gewicht)
      const nameSimilarity = calculateNameSimilarity(neuesPaket.name, entry.name);

      // 2️⃣ ADRESSE: streetAndNumber vs strasse+hausnummer (30% Gewicht)
      const addressSimilarity = calculateAddressSimilarity(
        neuesPaket.streetAndNumber, 
        entry.strasse, 
        entry.hausnummer
      );

      // 3️⃣ KOMBINIERTE ÄHNLICHKEIT (Name 70%, Adresse 30%)
      // Bei dauerhaften Adressbuch-Kontakten (Büro/Wohnanlage/Stammkunden) steht in strasse oft die Abteilung/Raum
      // oder gar nichts → bei starkem Namens-Match (>= 75%) zählt der Name allein voll.
      let combinedSimilarity = Math.round(
        nameSimilarity * 0.7 + addressSimilarity * 0.3
      );
      if (isPermanent && nameSimilarity >= 75) {
        combinedSimilarity = Math.max(combinedSimilarity, nameSimilarity);
      }

      console.log(`📬 [Matching] ${entry.name}: Name=${nameSimilarity}%, Adresse=${addressSimilarity}%, Gesamt=${combinedSimilarity}%`);

      // 4️⃣ SCHWELLWERT: >= 50% (genug Toleranz für OCR-Fehler, wenig Fehlalarme)
      if (combinedSimilarity >= 50) {
        matches.push({
          ...entry,
          id: entry.firebase_id || entry.id,
          email: entry.email || entry.kontakt || '',
          note: entryNote,
          shelf: entry.shelf || (isPermanent ? entry.hausnummer : '') || '',
          kontaktTyp: entry.kontaktTyp || entry.kontakt_typ || 'email',
          erstellt: entry.erstellt || entry.created_at || new Date().toISOString(),
          isAddressBook: isPermanent,
          matchScore: combinedSimilarity,
          nameSimilarity,
          addressSimilarity,
          paketId: neuesPaket.id,
          paketName: neuesPaket.name,
          paketAdresse: neuesPaket.streetAndNumber
        });
      }
    }

    // Sortiere nach Match-Score (höchste zuerst)
    matches.sort((a, b) => b.matchScore - a.matchScore);

    console.log(`📬 [Matching] ${matches.length} passende Kunden gefunden`);
    return matches;

  } catch (error) {
    console.error('📬 [Matching] Fehler beim Matching:', error);
    return [];
  }
}

// ============================================
// 🔧 HILFSFUNKTIONEN
// ============================================

/**
 * Extrahiert die Hausnummer aus einer Adresse
 * "Musterstraße 42a" → "42a", "Hauptstr. 1" → "1"
 */
function extractHausnummer(adresse) {
  if (!adresse) return '';
  
  const normalized = adresse.toLowerCase().trim();
  
  const patterns = [
    /(?:str\.|straße|strasse|weg|platz|gasse|allee|ring|damm|ufer)\s*(\d+[a-zA-Z]?)/i,
    /(\d+[a-zA-Z]?)(?:\s*[-,]|\s*$)/,
    /\s(\d{1,4}[a-zA-Z]?)\s/,
  ];
  
  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match && match[1]) return match[1].toLowerCase();
  }
  
  const fallbackMatch = normalized.match(/(\d+[a-zA-Z]?)/);
  return fallbackMatch ? fallbackMatch[1] : '';
}

/**
 * Extrahiert den Straßennamen aus einer Adresse
 * "Musterstraße 42" → "Musterstraße"
 */
function extractStrasse(adresse) {
  if (!adresse) return '';
  return adresse.replace(/\s*\d+[a-zA-Z]?\s*$/, '').trim();
}

/**
 * Markiert einen Wartelisten-Eintrag als benachrichtigt.
 * Dauerhafte Adressbuch-Kontakte ('addr_...') behalten ihren Status 'dauerhaft',
 * damit auch zukünftige Pakete für diesen Mitarbeiter/Bewohner gemeldet werden.
 */
export async function markAsNotified(waitingEntryId, paketId) {
  try {
    const isPermanent = String(waitingEntryId || '').startsWith('addr_');
    console.log(`📬 [Notification] Markiere ${waitingEntryId} als benachrichtigt für Paket ${paketId} (dauerhaft=${isPermanent})`);
    
    await patchWaitingListEntry(waitingEntryId, {
      status: isPermanent ? 'dauerhaft' : 'benachrichtigt',
      paketId: paketId,
      benachrichtigt: true
    });

    console.log(`📬 [Notification] Status aktualisiert`);
    return true;
  } catch (error) {
    console.error('📬 [Notification] Fehler beim Markieren:', error);
    return false;
  }
}

/**
 * Lädt alle wartenden Kunden aus der Warteliste
 */
export async function getWaitingList() {
  try {
    const allEntries = await serverGetWaitingList();
    return allEntries.filter(e => e.status === 'wartend');
  } catch (error) {
    console.error('📬 Fehler beim Laden der Warteliste:', error);
    return [];
  }
}
